import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { scaleMotionProps2 } from 'constants/animationProps';
import fundmoreai from 'assets/fundmoreai.png';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/features/onboarding-layout.module.css';
/**
 * Renders the Page1 component.
 *
 * @param {Object} props - The component props.
 * @param {string} props.user - The user name.
 * @param {boolean} props.selectedOption - The selected option.
 * @param {function} props.setPage - The function to set the current page.
 * @param {function} props.setSelectedOption - The function to set the selected option.
 * @param {function} props.setAddress - The function to set the address.
 * @param {function} props.setSelectedOfficer - The function to set the selected officer.
 * @param {function} props.setSelectedOfficerObj - The function to set the selected officer object.
 * @param {function} props.setLoanPurpose - The function to set the loan purpose.
 * @param {function} props.setEmployment - The function to set the employment.
 * @param {Object} props.data - The data object.
 * @param {boolean} props.isLoading - Indicates if the data is loading.
 * @param {boolean} props.setDirection - The function to set the direction.
 * @param {boolean} props.direction - The direction.
 * @returns {JSX.Element} The rendered Page1 component.
 */
const Page1 = ({
  user,
  selectedOption,
  setPage,
  setSelectedOption,
  setAddress,
  setSelectedOfficer,
  setSelectedOfficerObj,
  setLoanPurpose,
  setEmployment,
  data,
  isLoading,
  setDirection,
  direction
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const handleRadioChange = (option) => {
    setSelectedOption(option === selectedOption ? null : option);
  };
  const clearData = () => {
    if (data?.isWorkingWithAnyLoanOfficer !== selectedOption) {
      setAddress('');
      setSelectedOfficer('');
      setSelectedOfficerObj(null);
      setLoanPurpose('');
      setEmployment('');
      setDirection(null);
    }
  };

  return (
    <div className={styles.contentWrapper}>
      <AnimatePresence mode="wait">
        <motion.div
          {...scaleMotionProps2}
          className={`${styles.formContainer} ${styles.fadeInContent}`}
        >
          <div className={styles.logoContainer}>
            <img src={fundmoreai} alt="Fundmore" className={styles.logoIcon} />
            <div className={styles.logoText}>
              <span className={styles.logoTextLight}>fund</span>
              <span className={styles.logoTextBold}>more</span>
              <span className={styles.logoTextGreen}>.ai</span>
            </div>
          </div>
          <span className={styles.srOnly}>
            {t('onboarding:greeting', { name: user })}
          </span>

          <div className={styles.progressContainer}>
            <div className={styles.progressBar} />
          </div>

          <h2 className={styles.heading}>
            {t('onboarding:loanOfficerQuestion')}
          </h2>
          <p className={styles.subheading}>
            {t('onboarding:loanOfficerSubtitle')}
          </p>

          <div className={styles.optionsContainer}>
            <div
              className={`${styles.optionCard} ${
                selectedOption === true
                  ? styles.optionCardSelected
                  : styles.optionCardDefault
              }`}
              onClick={() => handleRadioChange(true)}
            >
              <span className={styles.optionLabel}>
                {t('common:yes')}
              </span>
              <div>
                {selectedOption === true ? (
                  <div className={styles.choiceIconSelected}>
                    <CheckCircle size={20} className={styles.choiceIconSelectedMark} />
                  </div>
                ) : (
                  <Circle size={24} className={styles.choiceIconUnselected} />
                )}
              </div>
            </div>
            <div
              className={`${styles.optionCard} ${
                selectedOption === false
                  ? styles.optionCardSelected
                  : styles.optionCardDefault
              }`}
              onClick={() => handleRadioChange(false)}
            >
              <span className={styles.optionLabel}>
                {t('common:no')}
              </span>
              <div>
                {selectedOption === false ? (
                  <div className={styles.choiceIconSelected}>
                    <CheckCircle size={20} className={styles.choiceIconSelectedMark} />
                  </div>
                ) : (
                  <Circle size={24} className={styles.choiceIconUnselected} />
                )}
              </div>
            </div>
          </div>

          <div className={`${styles.buttonContainer} ${styles.buttonGroup}`}>
            <button
              type="button"
              onClick={() => {
                setDirection(false);
              }}
              className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonFlexOne} ${styles.buttonMuted}`}
            >
              {t('common:buttons.back')}
            </button>
            <button
              type="button"
              disabled={selectedOption === null}
              onClick={
                selectedOption
                  ? () => {
                      clearData();
                      setPage(3);
                      setAddress('');
                      setDirection(true);
                    }
                  : () => {
                      clearData();
                      setPage(2);
                      setSelectedOfficer('');
                      setDirection(true);
                    }
              }
              className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonFlexTwo}`}
            >
              {isLoading && direction
                ? t('common:buttons.pleaseWait')
                : t('common:buttons.continue')}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Page1;
